import { revisionedRead } from './readCache';
import type { PostgrestError, SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClient } from './supabase';
import { fetchEventTotalsBatched } from './eventTotals';
import {
  eventDetailToUpsertInput,
  rowToEventDetail,
  rowToEventSummary,
  type EventDetail,
  type EventSummary,
} from '../domain/events';
import { isMissingColumnError } from '../utils/supabaseErrors';

type ClientOption = { supabase?: SupabaseClient };
type EventSearchProfileRow = {
  id?: string | null;
  auth_user_id?: string | null;
  full_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
};
type EventLibraryRelatedSearchMatches = {
  venueEventIds: string[];
  clientIds: string[];
};

const defaultSelect =
  'id,event_code,name,status,customer_pickup,client_id,site_location_id,event_start_date,event_end_date,notes';
const eventCalendarVisibilityColumn = 'hide_from_event_calendar';
const eventCalendarWindowRpc = 'event_calendar_events_v1';
const listSelectLegacy =
  'id,name,event_code,r2_contract_iid,show_start_date,show_end_date,event_start_date,event_end_date,fill_date,ship_date,load_in_date,setup_date,rehearsal_date,strike_date,load_out_date,return_date,hide_warehouse_tasks,customer_pickup,status,site_location_id,order_id,client_business,client_id,tax_subtotal,created_at,clients:client_id(business_name),event_venues:event_venues_event_id_fkey(venues(venue_name)),project_manager_profile_id,sales_manager_id,project_manager:project_manager_profile_id(full_name,first_name,last_name)';
const listSelect =
  'id,name,event_code,r2_contract_iid,show_start_date,show_end_date,event_start_date,event_end_date,fill_date,ship_date,load_in_date,setup_date,rehearsal_date,strike_date,load_out_date,return_date,hide_warehouse_tasks,hide_from_event_calendar,customer_pickup,status,site_location_id,order_id,client_business,client_id,tax_subtotal,created_at,clients:client_id(business_name),event_venues:event_venues_event_id_fkey(venues(venue_name)),project_manager_profile_id,sales_manager_id,project_manager:project_manager_profile_id(full_name,first_name,last_name)';

export function isMissingEventCalendarVisibilityColumn(error: unknown): boolean {
  if (!isMissingColumnError(error, eventCalendarVisibilityColumn)) return false;
  if (!error || typeof error !== 'object') return false;
  const candidate = error as { message?: unknown; details?: unknown; hint?: unknown };
  return [candidate.message, candidate.details, candidate.hint]
    .filter((part): part is string => typeof part === 'string' && part.length > 0)
    .join(' ')
    .toLowerCase()
    .includes(eventCalendarVisibilityColumn);
}

function isMissingEventCalendarWindowRpc(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as { code?: unknown; message?: unknown; details?: unknown; hint?: unknown };
  const code = String(candidate.code ?? '');
  const text = [candidate.message, candidate.details, candidate.hint]
    .filter((part): part is string => typeof part === 'string' && part.length > 0)
    .join(' ')
    .toLowerCase();
  return code === '42883'
    || code === 'PGRST202'
    || (
      text.includes(eventCalendarWindowRpc)
      && (
        text.includes('does not exist')
        || text.includes('could not find')
        || text.includes('schema cache')
      )
    );
}

function eventCalendarSource(
  supabase: SupabaseClient,
  windowStart: string,
  windowEnd: string,
  includeWarehouseTasks: boolean,
  count?: 'planned',
): any {
  return supabase.rpc(eventCalendarWindowRpc as any, {
    p_window_start: windowStart,
    p_window_end: windowEnd,
    p_include_warehouse_tasks: includeWarehouseTasks,
  }, count ? { count } : undefined);
}

export type EventListRow = {
  id: string;
  name: string | null;
  event_code: string;
  r2_contract_iid?: string | null;
  show_start_date: string | null;
  show_end_date?: string | null;
  event_start_date?: string | null;
  event_end_date?: string | null;
  /** Warehouse prep fill date (earliest logistics date). */
  fill_date?: string | null;
  /** Warehouse ship date. */
  ship_date?: string | null;
  /** On-site load-in date; calendar bars prefer this as the core start when set. */
  load_in_date?: string | null;
  /** Intermediate setup milestone; used to infer the event window before detail hydration. */
  setup_date?: string | null;
  /** Intermediate rehearsal milestone; used to infer the event window before detail hydration. */
  rehearsal_date?: string | null;
  /** On-site strike date; used as a core end fallback when event_end_date is not persisted yet. */
  strike_date?: string | null;
  /** On-site pickup/load-out date; calendar bars prefer this as the core end when set. */
  load_out_date?: string | null;
  /** Warehouse return date (latest logistics date). */
  return_date?: string | null;
  /** Hide generated warehouse prep/ship/return tasks for this event. */
  hide_warehouse_tasks?: boolean | null;
  /** Hide this event from the Events library calendar view only. */
  hide_from_event_calendar?: boolean | null;
  /** Current fulfillment mode. Customer-facing UI spells this out as Customer Pickup. */
  customer_pickup?: boolean | null;
  status: string | null;
  /** Event site location (events.site_location_id); use this for availability, never orders table. */
  site_location_id?: string | null;
  /** Legacy order snapshot link. Stage and fulfillment logic must use event_* tables. */
  order_id?: string | null;
  client_business?: string | null;
  /** Resolved client name: client_business when set, else clients.business_name when client_id is set. */
  client_display_name?: string | null;
  event_venues?: { venues?: { venue_name?: string | null } | null }[] | null;
  /** Order stage comes from events.status only (event-level). Orders relation no longer loaded for list. */
  event_lines?: Array<{ event_line_id?: string; item_id?: string | null; qty?: number | null; kit_anchor?: boolean }> | null;
  /** Event subtotal (items + labor, post-discount, pre-tax) from get_event_totals_batch. */
  net_event_amount?: number | null;
  /** Persisted event tax subtotal, preserved from R2 for imported events. */
  tax_subtotal?: number | null;
  /** Customer-facing event total: post-discount subtotal plus tax subtotal. */
  customer_total_amount?: number | null;
  /** Number of event lines (items) on the event. */
  item_count?: number;
  /** 0–1 ratio of lines that are fully available; set by availability hook. */
  availability_ratio?: number | null;
  project_manager_profile_id?: string | null;
  sales_manager_id?: string | null;
  /** Project manager display name (from user_profiles). */
  project_manager_display_name?: string | null;
  /** Sales person display name (resolved from auth id via user_profiles; set client-side). */
  sales_manager_display_name?: string | null;
  /** Row creation timestamp. */
  created_at?: string | null;
};

type EventCalendarDateSource = Pick<
  EventListRow,
  | 'load_in_date'
  | 'setup_date'
  | 'rehearsal_date'
  | 'show_start_date'
  | 'show_end_date'
  | 'strike_date'
  | 'load_out_date'
  | 'event_start_date'
  | 'event_end_date'
>;

const EVENT_WINDOW_MILESTONE_DATE_KEYS = [
  'load_in_date',
  'setup_date',
  'rehearsal_date',
  'show_start_date',
  'show_end_date',
  'strike_date',
  'load_out_date',
] as const;

function normalizeEventListDate(value?: string | null): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const datePart = trimmed.split('T')[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : null;
}

function minDate(values: Array<string | null>): string | null {
  const dates = values.filter((value): value is string => Boolean(value));
  if (!dates.length) return null;
  return [...dates].sort()[0] ?? null;
}

function maxDate(values: Array<string | null>): string | null {
  const dates = values.filter((value): value is string => Boolean(value));
  if (!dates.length) return null;
  const sorted = [...dates].sort();
  return sorted[sorted.length - 1] ?? null;
}

function finiteNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function uniqueStrings(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const normalized = value.trim();
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(normalized);
  }
  return result;
}

function searchTokens(value: string): string[] {
  return uniqueStrings(value.split(/\s+/));
}

function escapePostgrestLikePattern(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_');
}

function parseEventLibraryRelatedSearchMatches(value: unknown): EventLibraryRelatedSearchMatches {
  const payload = Array.isArray(value) && value.length === 1 ? value[0] : value;
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return { venueEventIds: [], clientIds: [] };
  }
  const record = payload as Record<string, unknown>;
  const stringIds = (candidate: unknown): string[] => (
    Array.isArray(candidate)
      ? uniqueStrings(candidate.filter((id): id is string => typeof id === 'string'))
      : []
  );
  return {
    venueEventIds: stringIds(record.venue_event_ids),
    clientIds: stringIds(record.client_ids),
  };
}

function isMissingEventLibraryRelatedSearchRpc(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const candidate = error as { code?: unknown; message?: unknown };
  const code = String(candidate.code ?? '');
  const message = String(candidate.message ?? '').toLowerCase();
  return code === '42883'
    || code === 'PGRST202'
    || (
      message.includes('event_library_related_search_matches')
      && (
        message.includes('does not exist')
        || message.includes('could not find')
        || message.includes('schema cache')
      )
    );
}

function buildUserProfileSearchOrFilter(search: string): string {
  const trimmed = search.trim();
  const tokens = searchTokens(trimmed);
  const fullTextTerms = uniqueStrings([trimmed, ...tokens]);
  const parts: string[] = [];
  for (const term of fullTextTerms) {
    const like = `%${escapePostgrestLikePattern(term)}%`;
    parts.push(`full_name.ilike.${like}`);
    parts.push(`email.ilike.${like}`);
  }
  for (const token of tokens) {
    const like = `%${escapePostgrestLikePattern(token)}%`;
    parts.push(`first_name.ilike.${like}`);
    parts.push(`last_name.ilike.${like}`);
  }
  return uniqueStrings(parts).join(',');
}

function profileMatchesEventSearch(row: EventSearchProfileRow, search: string): boolean {
  const tokens = searchTokens(search).map((token) => token.toLowerCase());
  if (!tokens.length) return true;
  const first = (row.first_name ?? '').trim();
  const last = (row.last_name ?? '').trim();
  const haystack = [
    row.full_name ?? '',
    first,
    last,
    `${first} ${last}`.trim(),
    row.email ?? '',
  ].join(' ').toLowerCase();
  return tokens.every((token) => haystack.includes(token));
}

export function calculateEventCustomerTotalAmount(
  postDiscountAmount: unknown,
  taxSubtotal: unknown,
): number | null {
  const postDiscount = finiteNumber(postDiscountAmount);
  if (postDiscount == null) return null;
  const tax = finiteNumber(taxSubtotal) ?? 0;
  return postDiscount + tax;
}

export function eventCustomerTotalAmount(
  event: Pick<EventListRow, 'customer_total_amount' | 'net_event_amount' | 'tax_subtotal'>,
): number | null {
  const explicit = finiteNumber(event.customer_total_amount);
  if (explicit != null) return explicit;
  return calculateEventCustomerTotalAmount(event.net_event_amount, event.tax_subtotal);
}

export function deriveEventCalendarDateRange(event: EventCalendarDateSource): {
  startDate: string | null;
  endDate: string | null;
} {
  const loadInDate = normalizeEventListDate(event.load_in_date);
  const showStartDate = normalizeEventListDate(event.show_start_date);
  const showEndDate = normalizeEventListDate(event.show_end_date);
  const strikeDate = normalizeEventListDate(event.strike_date);
  const loadOutDate = normalizeEventListDate(event.load_out_date);
  const eventStartDate = normalizeEventListDate(event.event_start_date);
  const eventEndDate = normalizeEventListDate(event.event_end_date);
  const computedMilestoneStart = minDate(
    EVENT_WINDOW_MILESTONE_DATE_KEYS.map((key) => normalizeEventListDate(event[key])),
  );
  const computedMilestoneEnd = maxDate(
    EVENT_WINDOW_MILESTONE_DATE_KEYS.map((key) => normalizeEventListDate(event[key])),
  );

  const startDate = loadInDate || eventStartDate || showStartDate || computedMilestoneStart;
  const rawEndDate = loadOutDate || eventEndDate || showEndDate || strikeDate || computedMilestoneEnd || startDate;
  const endDate = startDate && rawEndDate && rawEndDate < startDate ? startDate : rawEndDate;

  return { startDate, endDate };
}

/** Status filter: 'all' = no filter, 'none' = only events with no status, { in: string[] } = status in list */
export type EventListStatusFilter = 'all' | 'none' | { in: string[] };

export type EventListFilters = {
  search?: string;
  sortBy?: 'latest' | 'r2' | 'name' | 'date' | 'endDate' | 'stage' | 'createdDate';
  sortDir?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
  pageSize?: number;
  /** Filter by site (events.site_location_id). Omit or 'all' = no filter. */
  siteLocationId?: string | null;
  /** Filter by multiple sites (events.site_location_id in list). Used for region filtering. */
  siteLocationIds?: string[];
  /** Filter by event status so count and page reflect all matching events. */
  statusFilter?: EventListStatusFilter;
  /** Date range filters (YYYY-MM-DD). Applied to event_start_date / event_end_date. */
  startDateFrom?: string;
  startDateTo?: string;
  endDateFrom?: string;
  endDateTo?: string;
  /** Filter by project manager profile id(s) (user_profiles.id). */
  pmProfileIds?: string[];
  /** Filter by sales person auth id(s) (auth.users.id). */
  spAuthIds?: string[];
  /** Filter by the event's current fulfillment mode. */
  customerPickup?: boolean;
  /** Skip the event_order_total secondary query to save bandwidth when totals aren't displayed (e.g. grid view). */
  skipTotals?: boolean;
  /**
   * Calendar overlap filter (YYYY-MM-DD). When set, fetches events whose
   * on-site window, show/event window, or warehouse whisker span touches the
   * visible calendar window.
   */
  calendarWindowStart?: string;
  calendarWindowEnd?: string;
  /** Calendar-only option: include warehouse prep/ship/return whisker spans in the visible window query. */
  includeWarehouseTasksInCalendar?: boolean;
  /** Calendar-only option: event calendar visibility filter for temporary admin/manager reveal flows. */
  calendarVisibility?: 'visible' | 'hidden' | 'all';
  /** Include only overdue events according to event_overdue_status. */
  overdueOnly?: boolean;
  /** Full scoped badge counts; independent of the currently selected flag and page. */
  includeFlagCounts?: boolean;
  /** Include only urgent events according to event_overdue_status. */
  urgentOnly?: boolean;
};

export type EventCustomerTotalSummary = {
  count: number;
  total: number;
};

export type EventCustomerTotalSummaryFilters = Pick<
  EventListFilters,
  'siteLocationId' | 'siteLocationIds' | 'statusFilter' | 'customerPickup' | 'calendarWindowStart' | 'calendarWindowEnd' | 'includeWarehouseTasksInCalendar' | 'calendarVisibility'
  | 'search' | 'startDateFrom' | 'startDateTo' | 'endDateFrom' | 'endDateTo'
  | 'pmProfileIds' | 'spAuthIds' | 'overdueOnly' | 'urgentOnly'
>;

export function buildCalendarWindowOverlapFilter(
  calendarWindowStart: string,
  calendarWindowEnd: string,
  includeWarehouseTasks = true,
): string {
  const warehouseTasksVisible = 'hide_warehouse_tasks.is.false';
  const eventWindowFilters = [
    `and(load_in_date.lte.${calendarWindowEnd},load_out_date.gte.${calendarWindowStart})`,
    `and(load_in_date.lte.${calendarWindowEnd},load_out_date.is.null,event_end_date.is.null,show_end_date.is.null,strike_date.gte.${calendarWindowStart})`,
    `and(load_in_date.lte.${calendarWindowEnd},load_out_date.is.null,event_end_date.gte.${calendarWindowStart})`,
    `and(load_in_date.lte.${calendarWindowEnd},load_out_date.is.null,event_end_date.is.null,show_end_date.gte.${calendarWindowStart})`,
    `and(load_in_date.gte.${calendarWindowStart},load_in_date.lte.${calendarWindowEnd},load_out_date.is.null,event_end_date.is.null,show_end_date.is.null)`,
    `and(load_in_date.is.null,event_start_date.lte.${calendarWindowEnd},event_end_date.gte.${calendarWindowStart})`,
    `and(load_in_date.is.null,event_start_date.lte.${calendarWindowEnd},event_end_date.is.null,show_end_date.is.null,strike_date.gte.${calendarWindowStart})`,
    `and(load_in_date.is.null,event_start_date.lte.${calendarWindowEnd},event_end_date.is.null,show_end_date.gte.${calendarWindowStart})`,
    `and(load_in_date.is.null,event_start_date.gte.${calendarWindowStart},event_start_date.lte.${calendarWindowEnd},event_end_date.is.null,show_end_date.is.null)`,
    `and(load_in_date.is.null,event_start_date.is.null,show_start_date.lte.${calendarWindowEnd},show_end_date.gte.${calendarWindowStart})`,
    `and(load_in_date.is.null,event_start_date.is.null,show_start_date.lte.${calendarWindowEnd},show_end_date.is.null,strike_date.gte.${calendarWindowStart})`,
    `and(load_in_date.is.null,event_start_date.is.null,show_start_date.gte.${calendarWindowStart},show_start_date.lte.${calendarWindowEnd},show_end_date.is.null)`,
    `and(setup_date.lte.${calendarWindowEnd},strike_date.gte.${calendarWindowStart})`,
    `and(setup_date.lte.${calendarWindowEnd},load_out_date.gte.${calendarWindowStart})`,
    `and(rehearsal_date.lte.${calendarWindowEnd},strike_date.gte.${calendarWindowStart})`,
    `and(rehearsal_date.lte.${calendarWindowEnd},load_out_date.gte.${calendarWindowStart})`,
    `and(setup_date.gte.${calendarWindowStart},setup_date.lte.${calendarWindowEnd})`,
    `and(rehearsal_date.gte.${calendarWindowStart},rehearsal_date.lte.${calendarWindowEnd})`,
    `and(strike_date.gte.${calendarWindowStart},strike_date.lte.${calendarWindowEnd})`,
    `and(load_out_date.gte.${calendarWindowStart},load_out_date.lte.${calendarWindowEnd})`,
  ];
  if (!includeWarehouseTasks) return eventWindowFilters.join(',');
  return [
    ...eventWindowFilters,
    `and(${warehouseTasksVisible},fill_date.lte.${calendarWindowEnd},load_in_date.gte.${calendarWindowStart})`,
    `and(${warehouseTasksVisible},ship_date.lte.${calendarWindowEnd},load_in_date.gte.${calendarWindowStart})`,
    `and(${warehouseTasksVisible},fill_date.lte.${calendarWindowEnd},load_in_date.is.null,event_start_date.gte.${calendarWindowStart})`,
    `and(${warehouseTasksVisible},ship_date.lte.${calendarWindowEnd},load_in_date.is.null,event_start_date.gte.${calendarWindowStart})`,
    `and(${warehouseTasksVisible},fill_date.lte.${calendarWindowEnd},load_in_date.is.null,event_start_date.is.null,show_start_date.gte.${calendarWindowStart})`,
    `and(${warehouseTasksVisible},ship_date.lte.${calendarWindowEnd},load_in_date.is.null,event_start_date.is.null,show_start_date.gte.${calendarWindowStart})`,
    `and(${warehouseTasksVisible},return_date.gte.${calendarWindowStart},load_out_date.lte.${calendarWindowEnd})`,
    `and(${warehouseTasksVisible},return_date.gte.${calendarWindowStart},load_out_date.is.null,event_end_date.lte.${calendarWindowEnd})`,
    `and(${warehouseTasksVisible},return_date.gte.${calendarWindowStart},load_out_date.is.null,event_end_date.is.null,show_end_date.lte.${calendarWindowEnd})`,
    `and(${warehouseTasksVisible},return_date.gte.${calendarWindowStart},load_out_date.is.null,event_end_date.is.null,show_end_date.is.null,load_in_date.lte.${calendarWindowEnd})`,
    `and(${warehouseTasksVisible},return_date.gte.${calendarWindowStart},load_out_date.is.null,event_end_date.is.null,show_end_date.is.null,load_in_date.is.null,event_start_date.lte.${calendarWindowEnd})`,
    `and(${warehouseTasksVisible},return_date.gte.${calendarWindowStart},load_out_date.is.null,event_end_date.is.null,show_end_date.is.null,load_in_date.is.null,event_start_date.is.null,show_start_date.lte.${calendarWindowEnd})`,
  ].join(',');
}

export async function fetchEventCustomerTotalSummary(
  filters: EventCustomerTotalSummaryFilters = {},
  options?: ClientOption,
): Promise<{ data: EventCustomerTotalSummary; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const {
    siteLocationId,
    siteLocationIds,
    statusFilter,
    customerPickup,
    calendarWindowStart,
    calendarWindowEnd,
    includeWarehouseTasksInCalendar = true,
    calendarVisibility = 'visible',
  } = filters;
  const emptySummary: EventCustomerTotalSummary = { count: 0, total: 0 };

  if (statusFilter && typeof statusFilter === 'object' && statusFilter.in?.length === 0) {
    return { data: emptySummary, error: null };
  }

  const pageSize = 500;
  let afterId: string | null = null;
  let count = 0;
  let total = 0;
  let hasMore = true;
  while (hasMore) {
    // Keep the calendar's bounded RPC, and reuse the list predicate for every
    // non-calendar filter. Summary pages read only identity and persisted tax.
    const result: { data: unknown; error: PostgrestError | null } = calendarWindowStart && calendarWindowEnd
      ? await supabase.rpc('get_event_customer_summary_page' as any, {
      p_location_ids: siteLocationIds?.length ? siteLocationIds : siteLocationId && siteLocationId !== 'all' ? [siteLocationId] : null,
      p_statuses: typeof statusFilter === 'object' ? statusFilter?.in ?? null : null,
      p_status_none: statusFilter === 'none', p_customer_pickup: customerPickup ?? null,
      p_window_start: calendarWindowStart ?? null, p_window_end: calendarWindowEnd ?? null,
      p_include_warehouse_tasks: includeWarehouseTasksInCalendar,
      p_calendar_visibility: calendarVisibility, p_after_id: afterId, p_limit: pageSize,
    })
      : await fetchEventListRows(filters, { supabase, summaryAfterId: afterId });
    if (result.error) return { data: emptySummary, error: result.error };
    const rows = (Array.isArray(result.data) ? result.data : []) as Array<{ id: string; tax_subtotal: unknown }>;
    if (rows.length === 0) break;

    count += rows.length;
    const taxByEventId = new Map<string, number>();
    const eventIds = rows
      .map((row: { id?: string; tax_subtotal?: unknown }) => {
        const id = typeof row.id === 'string' ? row.id : '';
        if (id) taxByEventId.set(id, finiteNumber(row.tax_subtotal) ?? 0);
        return id;
      })
      .filter((id): id is string => id.length > 0);

    if (eventIds.length > 0) {
      const totalsResp = await fetchEventTotalsBatched(supabase, eventIds);
      if (totalsResp.error) return { data: { count, total }, error: totalsResp.error };
      if (Array.isArray(totalsResp.data)) {
        for (const row of totalsResp.data) {
          const eventId = typeof row?.event_id === 'string' ? row.event_id : '';
          if (!eventId) continue;
          total += calculateEventCustomerTotalAmount(
            row?.amount_post_discount,
            taxByEventId.get(eventId) ?? 0,
          ) ?? 0;
        }
      }
    }

    if (rows.length < pageSize) {
      hasMore = false;
    } else {
      const nextId = rows[rows.length - 1]?.id;
      if (typeof nextId !== 'string' || (afterId && nextId <= afterId)) {
        throw new Error('Event summary pagination did not advance.');
      }
      afterId = nextId;
    }
  }

  return { data: { count, total }, error: null };
}

export async function findEventByCode(
  code: string,
  excludeEventId?: string | null,
  options?: ClientOption,
): Promise<{ id: string; name: string | null; status: string | null } | null> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const lookup = (code || '').trim();
  if (!lookup) return null;
  const query = supabase
    .from('events')
    .select('id,name,status')
    .eq('event_code', lookup)
    .limit(1);
  const { data, error } = excludeEventId
    ? await query.neq('id', excludeEventId)
    : await query;
  if (error) throw error;
  const row = (data || [])[0];
  return row ? { id: row.id as string, name: (row as any).name ?? null, status: (row as any).status ?? null } : null;
}

export async function findNextAvailableOrderNumber(
  options?: ClientOption,
): Promise<string | null> {
  const supabase = options?.supabase ?? getSupabaseClient();
  // Find the highest numeric order code
  const { data, error } = await supabase
    .from('events')
    .select('event_code')
    .not('event_code', 'is', null)
    .order('event_code', { ascending: false })
    .limit(1);
  if (error) throw error;
  if (!data || data.length === 0) return '0000001';
  const lastCode = (data[0] as any).event_code;
  // Extract numeric part and increment
  const match = String(lastCode).match(/(\d+)$/);
  if (!match) return null;
  const num = parseInt(match[1], 10);
  if (isNaN(num)) return null;
  const nextNum = num + 1;
  // Format as 7-digit code (pad with zeros)
  return String(nextNum).padStart(7, '0');
}

export async function fetchEvents(options?: ClientOption): Promise<{
  data: EventSummary[];
  error: PostgrestError | null;
}> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('events')
    .select(defaultSelect)
    .order('start_at', { ascending: false });
  return {
    data: Array.isArray(data) ? data.map(rowToEventSummary) : [],
    error: error ?? null,
  };
}

/** Shared row predicate for the visible page and the complete filtered summary. */
async function fetchEventListRows(
  filters: EventListFilters = {},
  options?: ClientOption & { summaryAfterId?: string | null },
): Promise<{ data: any[]; count: number | null; error: PostgrestError | null; flagCounts?: { overdue: number; urgent: number } }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const {
    search = '',
    sortBy = 'latest',
    sortDir = 'desc',
    limit,
    offset,
    pageSize,
    siteLocationId,
    siteLocationIds,
    statusFilter,
    startDateFrom,
    startDateTo,
    endDateFrom,
    endDateTo,
    pmProfileIds,
    spAuthIds,
    customerPickup,
    calendarWindowStart,
    calendarWindowEnd,
    includeWarehouseTasksInCalendar = true,
    calendarVisibility = 'visible',
    overdueOnly = false,
    urgentOnly = false,
    includeFlagCounts = false,
  } = filters;

  // Support both old limit param and new pagination params
  const effectivePageSize = pageSize ?? limit ?? 50;
  const effectiveOffset = offset ?? 0;

  // Search across name, R2, client, notes, venue, PM, SP, and employee crew assignments.
  // Escape LIKE special chars for .or() string: % _ \
  const trimmedSearch = search.trim();
  let searchOrFilter: string | null = null;
  if (trimmedSearch) {
    const escaped = escapePostgrestLikePattern(trimmedSearch);
    const likeForOr = `%${escaped}%`;
    const searchPattern = `%${trimmedSearch}%`;
    const orParts: string[] = [
      `name.ilike.${likeForOr}`,
      `event_code.ilike.${likeForOr}`,
      `r2_contract_iid.ilike.${likeForOr}`,
      `client_business.ilike.${likeForOr}`,
      `notes.ilike.${likeForOr}`,
    ];
    // The guarded RPC checks staff access once before using the active-row
    // trigram indexes. Direct authenticated reads repeat duplicate RLS helpers
    // across the scan and can take several seconds even with those indexes.
    const [relatedResult, profileResult] = await Promise.all([
      supabase.rpc('event_library_related_search_matches' as any, {
        p_search_pattern: searchPattern,
      }),
      supabase
        .from('user_profiles')
        .select('id,auth_user_id,full_name,first_name,last_name,email')
        .or(buildUserProfileSearchOrFilter(trimmedSearch)),
    ]);
    let relatedMatches = parseEventLibraryRelatedSearchMatches(relatedResult.data);
    if (relatedResult.error && isMissingEventLibraryRelatedSearchRpc(relatedResult.error)) {
      // Safe rollout fallback for a client deployed before the database schema
      // cache sees the new function.
      const [venueResult, clientResult] = await Promise.all([
        supabase
          .from('event_venues')
          .select('event_id')
          .is('deleted_at', null)
          .ilike('venue_name', searchPattern),
        supabase
          .from('clients')
          .select('id')
          .is('deleted_at', null)
          .ilike('business_name', searchPattern),
      ]);
      relatedMatches = {
        venueEventIds: uniqueStrings(
          (venueResult.data ?? [])
            .map((row: { event_id?: string }) => row?.event_id ?? ''),
        ),
        clientIds: uniqueStrings(
          (clientResult.data ?? [])
            .map((row: { id?: string }) => row?.id ?? ''),
        ),
      };
    }
    const venueEventIds = relatedMatches.venueEventIds;
    if (venueEventIds.length > 0) orParts.push(`id.in.(${venueEventIds.join(',')})`);
    const clientIds = relatedMatches.clientIds;
    if (clientIds.length > 0) orParts.push(`client_id.in.(${clientIds.join(',')})`);
    const matchingProfiles = ((profileResult.data ?? []) as EventSearchProfileRow[])
      .filter((row) => profileMatchesEventSearch(row, trimmedSearch));
    const pmIds = uniqueStrings(matchingProfiles.map((r) => r.id ?? ''));
    if (pmIds.length > 0) {
      orParts.push(`project_manager_profile_id.in.(${pmIds.join(',')})`);
      orParts.push(`sales_person_profile_id.in.(${pmIds.join(',')})`);
    }
    const spSearchIds = uniqueStrings(matchingProfiles.map((r) => r.auth_user_id ?? ''));
    if (spSearchIds.length > 0) orParts.push(`sales_manager_id.in.(${spSearchIds.join(',')})`);
    if (pmIds.length > 0) {
      const crewResult = await supabase
        .from('event_labor')
        .select('event_id')
        .in('assigned_profile_id', pmIds)
        .in('assignment_status', ['assigned', 'offered', 'accepted']);
      const crewEventIds = uniqueStrings(
        (crewResult.data ?? [])
          .map((r: { event_id?: string }) => r?.event_id ?? ''),
      );
      if (crewEventIds.length > 0) orParts.push(`id.in.(${crewEventIds.join(',')})`);
    }
    searchOrFilter = orParts.join(',');
  }

  const buildQuery = async (
    includeEventCalendarVisibility: boolean,
    useCalendarWindowRpc: boolean,
    flagScope = { overdueOnly, urgentOnly },
    countOnly = false,
  ) => {
    const flagged = flagScope.overdueOnly || flagScope.urgentOnly;
    const calendarRpc = useCalendarWindowRpc && !flagged && !countOnly;
    const eventsTable: any = flagged
      ? supabase.rpc('event_library_flagged' as any, { p_overdue: flagScope.overdueOnly, p_urgent: flagScope.urgentOnly }, { count: 'exact', head: countOnly })
      : calendarRpc && calendarWindowStart && calendarWindowEnd
      ? eventCalendarSource(
          supabase,
          calendarWindowStart,
          calendarWindowEnd,
          includeWarehouseTasksInCalendar,
          'planned',
        )
      : supabase.from('events');
    const isSummary = options?.summaryAfterId !== undefined;
    let query: any = countOnly
      ? eventsTable.select('id', { count: 'exact', head: true })
      : isSummary
        ? eventsTable.select('id,tax_subtotal')
        : calendarRpc
      ? eventsTable.select(includeEventCalendarVisibility ? listSelect : listSelectLegacy)
      : includeEventCalendarVisibility
        ? eventsTable.select(listSelect, { count: 'exact' })
        : eventsTable.select(listSelectLegacy, { count: 'exact' });
    query = query.is('deleted_at', null);

    if (searchOrFilter) query = query.or(searchOrFilter);

    // Site filter: all matching events, not just current page
    if (siteLocationIds && siteLocationIds.length > 0) {
      query = query.in('site_location_id', siteLocationIds);
    } else if (siteLocationId && siteLocationId !== 'all') {
      query = query.eq('site_location_id', siteLocationId);
    }

    // Status filter: so count and pagination reflect all matching events
    if (statusFilter === 'none') {
      query = query.or('status.is.null,status.eq.');
    } else if (statusFilter && typeof statusFilter === 'object' && statusFilter.in?.length > 0) {
      query = query.in('status', statusFilter.in);
    }


    if (customerPickup != null) {
      query = query.eq('customer_pickup', customerPickup);
    }

    // Calendar overlap filter: include the on-site bar and warehouse whisker span.
    if (calendarWindowStart && calendarWindowEnd) {
      if (includeEventCalendarVisibility) {
        if (calendarVisibility === 'hidden') {
          query = query.eq(eventCalendarVisibilityColumn, true);
        } else if (calendarVisibility !== 'all') {
          query = query.eq(eventCalendarVisibilityColumn, false);
        }
      }
      if (!calendarRpc) {
        query = query.or(buildCalendarWindowOverlapFilter(calendarWindowStart, calendarWindowEnd, includeWarehouseTasksInCalendar));
      }
    } else {
      // Standard directional date range filters
      if (startDateFrom) query = query.gte('event_start_date', startDateFrom);
      if (startDateTo) query = query.lte('event_start_date', startDateTo);
      if (endDateFrom) query = query.gte('event_end_date', endDateFrom);
      if (endDateTo) query = query.lte('event_end_date', endDateTo);
    }

    // PM / SP filters (multi-select)
    if (pmProfileIds && pmProfileIds.length > 0) query = query.in('project_manager_profile_id', pmProfileIds);
    if (spAuthIds && spAuthIds.length > 0) query = query.in('sales_manager_id', spAuthIds);

    if (countOnly) return query;

    if (isSummary) {
      if (options?.summaryAfterId) query = query.gt('id', options.summaryAfterId);
      return query.order('id', { ascending: true }).limit(500);
    }

    const asc = sortDir === 'asc';
    if (sortBy === 'latest') query = query.order('created_at', { ascending: asc, nullsFirst: false });
    if (sortBy === 'r2') query = query.order('event_code', { ascending: asc, nullsFirst: false });
    if (sortBy === 'name') query = query.order('name', { ascending: asc, nullsFirst: true });
    if (sortBy === 'date') query = query.order('event_start_date', { ascending: asc, nullsFirst: false });
    if (sortBy === 'endDate') query = query.order('event_end_date', { ascending: asc, nullsFirst: false });
    if (sortBy === 'stage') query = query.order('status', { ascending: asc, nullsFirst: true });
    if (sortBy === 'createdDate') query = query.order('created_at', { ascending: asc, nullsFirst: false });

    // Equal dates/names must keep the same membership across page boundaries.
    query = query.order('id', { ascending: true });

    // Apply pagination after all filters so count is total matching
    return query.range(effectiveOffset, effectiveOffset + effectivePageSize - 1);
  };

  let useCalendarWindowRpc = Boolean(calendarWindowStart && calendarWindowEnd);
  let result = await buildQuery(true, useCalendarWindowRpc);
  if (result.error && useCalendarWindowRpc && isMissingEventCalendarWindowRpc(result.error)) {
    useCalendarWindowRpc = false;
    result = await buildQuery(true, false);
  }
  if (result.error && isMissingEventCalendarVisibilityColumn(result.error)) {
    if (calendarVisibility === 'hidden') {
      return { data: [], count: 0, error: null };
    }
    result = await buildQuery(false, useCalendarWindowRpc);
  }
  const { data, error, count } = result;
  if (error) {
    return { data: [], count: count ?? null, error };
  }

  const counts = includeFlagCounts ? await Promise.all([
    buildQuery(true, false, { overdueOnly: true, urgentOnly: false }, true),
    buildQuery(true, false, { overdueOnly: false, urgentOnly: true }, true),
  ]) : null;
  const flagCounts = counts && counts.every(row => !row.error && typeof row.count === 'number')
    ? { overdue: counts[0].count as number, urgent: counts[1].count as number } : undefined;
  return { data: Array.isArray(data) ? data : [], count: count ?? null, error: null, flagCounts };
}

export async function fetchEventList(
  filters: EventListFilters = {},
  options?: ClientOption,
): Promise<{ data: EventListRow[]; count: number | null; error: PostgrestError | null; flagCounts?: { overdue: number; urgent: number } }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data: rawRows, count, error, flagCounts } = await fetchEventListRows(filters, { supabase });
  if (error) return { data: [], count, error };
  const skipTotals = filters.skipTotals ?? false;
  const eventIds = rawRows.map((r: { id?: string }) => r?.id).filter((id): id is string => typeof id === 'string' && id.length > 0);

  const totalsByEventId: Map<string, number> = new Map();
  const itemCountsByEventId: Map<string, number> = new Map();
  if (eventIds.length > 0) {
    // Fetch item counts (always) and totals (only when needed) in parallel.
    // Use a small RPC or grouped query for counts to avoid PostgREST's default
    // 1000-row limit (which silently truncated counts for events with many lines).
    // Fetching event_id for each line and counting client-side was hitting the
    // limit when total lines across all visible events exceeded 1000.
    // Instead, fetch one row per event with its count.
    const itemCountsPromise = supabase
      .rpc('batch_event_line_counts' as any, { p_event_ids: eventIds });
    const totalsPromise = skipTotals
      ? Promise.resolve(null)
      : fetchEventTotalsBatched(supabase, eventIds);
    const [itemCountsResp, totalsResp] = await Promise.all([itemCountsPromise, totalsPromise]);
    if (itemCountsResp.error || totalsResp?.error) {
      return { data: [], count: count ?? null, error: itemCountsResp.error ?? totalsResp?.error ?? null };
    }
    if (totalsResp && Array.isArray(totalsResp.data)) {
      for (const row of totalsResp.data) {
        const eid = row?.event_id;
        const rawAmount = row?.amount_post_discount;
        // PostgREST may return numeric columns as strings; coerce safely
        const amount = typeof rawAmount === 'number' ? rawAmount : Number(rawAmount);
        if (typeof eid === 'string' && eid && Number.isFinite(amount)) {
          totalsByEventId.set(eid, amount);
        }
      }
    }
    if (Array.isArray(itemCountsResp.data)) {
      for (const row of itemCountsResp.data) {
        const eid = (row as { event_id?: string })?.event_id;
        const count = (row as { line_count?: number })?.line_count;
        if (typeof eid === 'string' && eid && typeof count === 'number') {
          itemCountsByEventId.set(eid, count);
        }
      }
    }
  }

  const rows = (rawRows as Array<EventListRow & {
    clients?: { business_name?: string | null } | null;
    project_manager?: { full_name?: string | null; first_name?: string | null; last_name?: string | null } | null;
  }>).map((row) => {
    const client_display_name = row.client_business ?? row.clients?.business_name ?? null;
    const { clients: _c, project_manager: pm, ...rest } = row;
    const totalFromView = row.id ? totalsByEventId.get(row.id) : undefined;
    const net_event_amount = totalFromView != null && Number.isFinite(totalFromView) ? totalFromView : undefined;
    const tax_subtotal = finiteNumber(row.tax_subtotal);
    const customer_total_amount = calculateEventCustomerTotalAmount(net_event_amount, tax_subtotal);
    const item_count = row.id ? (itemCountsByEventId.get(row.id) ?? 0) : 0;
    const project_manager_display_name = pm?.full_name?.trim()
      || ([(pm?.first_name ?? '').trim(), (pm?.last_name ?? '').trim()].join(' ').trim())
      || null;
    return {
      ...rest,
      hide_from_event_calendar: row.hide_from_event_calendar === true,
      client_display_name,
      net_event_amount: net_event_amount ?? undefined,
      tax_subtotal: tax_subtotal ?? undefined,
      customer_total_amount: customer_total_amount ?? undefined,
      item_count,
      project_manager_display_name: project_manager_display_name ?? undefined,
    } as EventListRow;
  });

  return {
    data: rows,
    count: count ?? null,
    error: null,
    flagCounts,
  };
}

export async function fetchEventById(eventId: string, options?: ClientOption): Promise<{
  data: EventDetail | null;
  error: PostgrestError | null;
}> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('events')
    .select(defaultSelect)
    .eq('id', eventId)
    .maybeSingle();
  return { data: data ? rowToEventDetail(data) : null, error: error ?? null };
}

export async function updateEventStatus(params: {
  eventId: string;
  status: string;
  supabase?: SupabaseClient;
}): Promise<PostgrestError | null> {
  const { eventId, status } = params;
  const supabase = params.supabase ?? getSupabaseClient();
  
  // Get current user's profile ID for updated_by
  const { data: profileId } = await supabase.rpc('current_user_profile_id').maybeSingle();
  
  const { error } = await supabase.from('events').update({ 
    status, 
    stage: status,
    updated_by: profileId || null,
  }).eq('id', eventId);
  return error ?? null;
}

export async function upsertEventDetail(params: {
  detail: EventDetail;
  supabase?: SupabaseClient;
}): Promise<{ data: EventDetail | null; error: PostgrestError | null }> {
  const { detail } = params;
  const supabase = params.supabase ?? getSupabaseClient();
  const payload = eventDetailToUpsertInput(detail);
  const { data, error } = await supabase.from('events').upsert(payload).select(defaultSelect).maybeSingle();
  return { data: data ? rowToEventDetail(data) : null, error: error ?? null };
}

export async function softDeleteEvent(params: {
  eventId: string;
  supabase?: SupabaseClient;
}): Promise<PostgrestError | null> {
  const supabase = params.supabase ?? getSupabaseClient();
  const { error } = await supabase.rpc('soft_delete', { table_name: 'events', _id: params.eventId });
  return error ?? null;
}

export type EventScheduleDates = {
  event_start_date: string | null;
  event_end_date: string | null;
  show_start_date: string | null;
  show_start_time: string | null;
  show_end_date: string | null;
  show_end_time: string | null;
  setup_date: string | null;
  setup_time: string | null;
  strike_date: string | null;
  strike_time: string | null;
  load_in_date: string | null;
  load_in_time: string | null;
  rehearsal_date: string | null;
  rehearsal_time: string | null;
  load_out_date: string | null;
  load_out_time: string | null;
};

export async function fetchEventScheduleDates(
  eventId: string,
  options?: { supabase?: SupabaseClient },
): Promise<{ data: EventScheduleDates | null; error: PostgrestError | null }> {
  return revisionedRead(options?.supabase ?? getSupabaseClient(), { eventId }, 'fetchEventScheduleDates', ["header","schedule"], async () => {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('events')
    .select(
      'event_start_date,event_end_date,show_start_date,show_start_time,show_end_date,show_end_time,' +
      'setup_date,setup_time,strike_date,strike_time,load_in_date,load_in_time,rehearsal_date,rehearsal_time,load_out_date,load_out_time',
    )
    .eq('id', eventId)
    .maybeSingle();
  if (error) return { data: null, error };
  return {
    data: data ? (data as unknown as EventScheduleDates) : {
      event_start_date: null,
      event_end_date: null,
      show_start_date: null,
      show_start_time: null,
      show_end_date: null,
      show_end_time: null,
      setup_date: null,
      setup_time: null,
      strike_date: null,
      strike_time: null,
      load_in_date: null,
      load_in_time: null,
      rehearsal_date: null,
      rehearsal_time: null,
      load_out_date: null,
      load_out_time: null,
    },
    error: null,
  };

  });
}

export type EventImportPayload = {
  name: string;
  event_code: string;
  show_start_date?: string | null;
  client_business?: string | null;
};

export async function insertEventFromImport(
  payload: EventImportPayload,
  options?: ClientOption,
): Promise<PostgrestError | null> {
  const supabase = options?.supabase ?? getSupabaseClient();
  
  // Get current user's profile ID for created_by
  const { data: profileId } = await supabase.rpc('current_user_profile_id').maybeSingle();
  
  const { error } = await supabase.from('events').insert({
    name: payload.name,
    event_code: payload.event_code,
    show_start_date: payload.show_start_date ?? null,
    client_business: payload.client_business ?? null,
    created_by: profileId || null,
  });
  return error ?? null;
}

export type EventDetailHeaderRecord = {
  name: string | null;
  event_code: string | null;
  r2_contract_iid?: string | null;
  client_business?: string | null;
  client_id?: string | null;
  /** Resolved client name: client_business when set, else clients.business_name when client_id is set. */
  client_display_name?: string | null;
  bill_to_business?: string | null;
  bill_to_client_id?: string | null;
  bill_to_is_different?: boolean | null;
  show_start_date?: string | null;
  show_end_date?: string | null;
  event_start_date?: string | null;
  event_end_date?: string | null;
  site_location_id?: string | null;
  show_start_time?: string | null;
  load_in_date?: string | null;
  load_in_time?: string | null;
  strike_date?: string | null;
  strike_time?: string | null;
  indoor_outdoor?: string | null;
  anticipated_attendees?: number | null;
  event_type?: string | null;
  status?: string | null;
  budget?: number | null;
  charge_days?: number | null;
  primary_contact_id?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  /** Reason for Lost/Cancelled; set when status is cancelled or void. */
  lost_cancelled_reason?: string | null;
  lost_reason_comment?: string | null;
  /** First client-facing Budgetary quote submission; null keeps that optional stage hidden. */
  budgetary_quote_submitted_at?: string | null;
  /** Active stage immediately before the event moved to Lost or Cancelled. */
  status_before_terminal?: string | null;
  /** Profile ID of user who unlocked an invoiced event; null = locked. */
  invoice_unlocked_by?: string | null;
  /** Sales person profile ID assigned to this event. */
  sales_person_profile_id?: string | null;
  /** Project manager profile ID assigned to this event. */
  project_manager_profile_id?: string | null;
  /** Resolved project manager display name. */
  project_manager_display_name?: string | null;
  /** Current Customer Pickup fulfillment mode; lifecycle stage and completed evidence remain event-scoped. */
  customer_pickup?: boolean | null;
};

const eventDetailHeaderBaseSelect =
  'name,event_code,r2_contract_iid,client_business,client_id,bill_to_business,bill_to_client_id,bill_to_is_different,show_start_date,show_end_date,event_start_date,event_end_date,site_location_id,show_start_time,load_in_date,load_in_time,strike_date,strike_time,indoor_outdoor,anticipated_attendees,event_type,status,budget,charge_days,primary_contact_id,address_line1,address_line2,city,state,postal_code,country,lost_cancelled_reason,lost_reason_comment,invoice_unlocked_by,sales_person_profile_id,project_manager_profile_id,customer_pickup,clients:client_id(business_name),project_manager:project_manager_profile_id(full_name,first_name,last_name)';
const eventDetailHeaderSelect =
  `${eventDetailHeaderBaseSelect},budgetary_quote_submitted_at,status_before_terminal`;

function isMissingOptionalEventStageColumn(error: unknown): boolean {
  return isMissingColumnError(error, 'budgetary_quote_submitted_at')
    || isMissingColumnError(error, 'status_before_terminal');
}

export async function fetchEventDetailHeader(
  eventId: string,
  options?: ClientOption,
): Promise<{ data: EventDetailHeaderRecord | null; error: PostgrestError | null }> {
  return revisionedRead(options?.supabase ?? getSupabaseClient(), { eventId }, 'fetchEventDetailHeader', ["header","reference"], async () => {
  const supabase = options?.supabase ?? getSupabaseClient();
  const initialResult = await supabase
    .from('events')
    .select(eventDetailHeaderSelect)
    .eq('id', eventId)
    .maybeSingle();
  const { data, error } = initialResult.error && isMissingOptionalEventStageColumn(initialResult.error)
    ? await supabase
      .from('events')
      .select(eventDetailHeaderBaseSelect)
      .eq('id', eventId)
      .maybeSingle()
    : initialResult;
  if (error) return { data: null, error };
  const row = data as (EventDetailHeaderRecord & {
    clients?: { business_name?: string | null } | null;
    project_manager?: { full_name?: string | null; first_name?: string | null; last_name?: string | null } | null;
  }) | null;
  if (!row) return { data: null, error: null };
  const client_display_name = row.client_business ?? row.clients?.business_name ?? null;
  const project_manager_display_name = row.project_manager?.full_name?.trim()
    || ([(row.project_manager?.first_name ?? '').trim(), (row.project_manager?.last_name ?? '').trim()].join(' ').trim())
    || null;
  return {
    data: { ...row, client_display_name, project_manager_display_name } as EventDetailHeaderRecord,
    error: null,
  };

  });
}

export type EventCounts = { rooms: number; contacts: number; photos: number };

export async function fetchEventCounts(
  eventId: string,
  options?: ClientOption,
): Promise<{ data: EventCounts; error: PostgrestError | null }> {
  return revisionedRead(options?.supabase ?? getSupabaseClient(), { eventId }, 'fetchEventCounts', ["rooms","contacts","photos"], async () => {
  const supabase = options?.supabase ?? getSupabaseClient();
  const [roomsRes, contactsRes, photosRes] = await Promise.all([
    supabase.from('event_rooms').select('id', { count: 'exact', head: true }).eq('event_id', eventId).is('deleted_at', null),
    supabase.from('event_contacts').select('id', { count: 'exact', head: true }).eq('event_id', eventId).is('deleted_at', null),
    supabase.from('photos').select('id', { count: 'exact', head: true }).eq('event_id', eventId).is('deleted_at', null),
  ]);
  return {
    data: {
      rooms: roomsRes.count || 0,
      contacts: contactsRes.count || 0,
      photos: photosRes.count || 0,
    },
    error: roomsRes.error ?? contactsRes.error ?? photosRes.error ?? null,
  };

  });
}

export type EventScheduleRecord = {
  status: string | null;
  customer_pickup: boolean;
  r2_contract_iid: number | null;
  inventory_custody_mode: 'continuous' | 'scheduled_periods';
  fill_date: string | null;
  fill_time: string | null;
  ship_date: string | null;
  ship_time: string | null;
  hide_warehouse_tasks: boolean;
  hide_from_event_calendar: boolean;
  load_in_date: string | null;
  load_in_time: string | null;
  load_in_note: string | null;
  setup_date: string | null;
  setup_time: string | null;
  setup_note: string | null;
  rehearsal_date: string | null;
  rehearsal_time: string | null;
  rehearsal_note: string | null;
  strike_date: string | null;
  strike_time: string | null;
  strike_note: string | null;
  load_out_date: string | null;
  load_out_time: string | null;
  load_out_note: string | null;
  return_date: string | null;
  return_time: string | null;
  return_note: string | null;
  event_start_date: string | null;
  event_start_time: string | null;
  event_end_date: string | null;
  event_end_time: string | null;
  event_window_uses_milestones: boolean | null;
  show_start_date: string | null;
  show_start_time: string | null;
  show_end_date: string | null;
  show_end_time: string | null;
};

const eventScheduleSelectLegacy =
  'customer_pickup,fill_date,fill_time,ship_date,ship_time,hide_warehouse_tasks,load_in_date,load_in_time,load_in_note,setup_date,setup_time,setup_note,rehearsal_date,rehearsal_time,rehearsal_note,strike_date,strike_time,strike_note,load_out_date,load_out_time,load_out_note,return_date,return_time,return_note,event_start_date,event_start_time,event_end_date,event_end_time,show_start_date,show_start_time,show_end_date,show_end_time';
const eventScheduleSelectWithoutWindowSource =
  'customer_pickup,fill_date,fill_time,ship_date,ship_time,hide_warehouse_tasks,hide_from_event_calendar,load_in_date,load_in_time,load_in_note,setup_date,setup_time,setup_note,rehearsal_date,rehearsal_time,rehearsal_note,strike_date,strike_time,strike_note,load_out_date,load_out_time,load_out_note,return_date,return_time,return_note,event_start_date,event_start_time,event_end_date,event_end_time,show_start_date,show_start_time,show_end_date,show_end_time';
const eventScheduleSelect =
  `${eventScheduleSelectWithoutWindowSource},event_window_uses_milestones`;
const eventScheduleSelectWithInventoryCustody =
  `${eventScheduleSelect},status,r2_contract_iid,inventory_custody_mode`;

export async function fetchEventSchedule(
  eventId: string,
  options?: ClientOption,
): Promise<{ data: EventScheduleRecord | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  if (!eventId) return { data: null, error: null };
  let result = await supabase
    .from('events')
    .select(eventScheduleSelectWithInventoryCustody)
    .eq('id', eventId)
    .maybeSingle();
  if (
    result.error
    && (
      isMissingColumnError(result.error, 'inventory_custody_mode')
      || isMissingColumnError(result.error, 'r2_contract_iid')
    )
  ) {
    result = await supabase
      .from('events')
      .select(eventScheduleSelect)
      .eq('id', eventId)
      .maybeSingle();
  }
  if (result.error && isMissingColumnError(result.error, 'event_window_uses_milestones')) {
    result = await supabase
      .from('events')
      .select(eventScheduleSelectWithoutWindowSource)
      .eq('id', eventId)
      .maybeSingle();
  }
  if (result.error && isMissingEventCalendarVisibilityColumn(result.error)) {
    result = await supabase
      .from('events')
      .select(eventScheduleSelectLegacy)
      .eq('id', eventId)
      .maybeSingle();
  }
  const { data, error } = result;
  if (error) return { data: null, error };
  const record: EventScheduleRecord = {
    status: typeof data?.status === 'string' ? data.status : null,
    customer_pickup: data?.customer_pickup === true,
    r2_contract_iid:
      data?.r2_contract_iid == null || !Number.isFinite(Number(data.r2_contract_iid))
        ? null
        : Number(data.r2_contract_iid),
    inventory_custody_mode:
      data?.inventory_custody_mode === 'scheduled_periods' ? 'scheduled_periods' : 'continuous',
    fill_date: data?.fill_date ?? null,
    fill_time: data?.fill_time ?? null,
    ship_date: data?.ship_date ?? null,
    ship_time: data?.ship_time ?? null,
    hide_warehouse_tasks: data?.hide_warehouse_tasks === true,
    hide_from_event_calendar: data?.hide_from_event_calendar === true,
    load_in_date: data?.load_in_date ?? null,
    load_in_time: data?.load_in_time ?? null,
    load_in_note: data?.load_in_note ?? null,
    setup_date: data?.setup_date ?? null,
    setup_time: data?.setup_time ?? null,
    setup_note: data?.setup_note ?? null,
    rehearsal_date: data?.rehearsal_date ?? null,
    rehearsal_time: data?.rehearsal_time ?? null,
    rehearsal_note: data?.rehearsal_note ?? null,
    strike_date: data?.strike_date ?? null,
    strike_time: data?.strike_time ?? null,
    strike_note: data?.strike_note ?? null,
    load_out_date: data?.load_out_date ?? null,
    load_out_time: data?.load_out_time ?? null,
    load_out_note: data?.load_out_note ?? null,
    return_date: data?.return_date ?? null,
    return_time: data?.return_time ?? null,
    return_note: data?.return_note ?? null,
    event_start_date: data?.event_start_date ?? null,
    event_start_time: data?.event_start_time ?? null,
    event_end_date: data?.event_end_date ?? null,
    event_end_time: data?.event_end_time ?? null,
    event_window_uses_milestones:
      typeof data?.event_window_uses_milestones === 'boolean'
        ? data.event_window_uses_milestones
        : null,
    show_start_date: data?.show_start_date ?? null,
    show_start_time: data?.show_start_time ?? null,
    show_end_date: data?.show_end_date ?? null,
    show_end_time: data?.show_end_time ?? null,
  };
  return { data: record, error: null };
}

export async function updateEventSchedule(
  eventId: string,
  payload: Partial<Omit<EventScheduleRecord, 'status' | 'customer_pickup' | 'r2_contract_iid' | 'inventory_custody_mode'>>,
  options?: ClientOption,
): Promise<PostgrestError | null> {
  const supabase = options?.supabase ?? getSupabaseClient();
  if (!eventId) return null;
  
  // Get current user's profile ID for updated_by
  const { data: profileId } = await supabase.rpc('current_user_profile_id').maybeSingle();
  
  const updatePayload = {
    ...payload,
    updated_by: profileId || null,
  };
  
  const { error } = await supabase.from('events').update(updatePayload).eq('id', eventId);
  return error ?? null;
}

export type EventVenueAddress = {
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  is_outdoor?: boolean | null;
};

export async function fetchEventPrimaryVenueAddress(
  eventId: string,
  options?: ClientOption,
): Promise<{ data: EventVenueAddress | null; error: PostgrestError | null }> {
  return revisionedRead(options?.supabase ?? getSupabaseClient(), { eventId }, 'fetchEventPrimaryVenueAddress', ["venues","reference"], async () => {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('event_venues')
    .select('venues!inner(address_line1,address_line2,city,state,postal_code,country,is_outdoor)')
    .eq('event_id', eventId)
    .limit(1)
    .maybeSingle();
  if (error) return { data: null, error };
  const venue = (data as any)?.venues || null;
  if (!venue) return { data: null, error: null };
  return {
    data: {
      address_line1: venue.address_line1 ?? null,
      address_line2: venue.address_line2 ?? null,
      city: venue.city ?? null,
      state: venue.state ?? null,
      postal_code: venue.postal_code ?? null,
      country: venue.country ?? null,
      is_outdoor: typeof venue.is_outdoor === 'boolean' ? venue.is_outdoor : null,
    },
    error: null,
  };

  });
}

export async function fetchEventPrimaryVenueName(
  eventId: string,
  options?: ClientOption,
): Promise<{ data: string | null; error: PostgrestError | null }> {
  return revisionedRead(options?.supabase ?? getSupabaseClient(), { eventId }, 'fetchEventPrimaryVenueName', ["venues","reference"], async () => {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('event_venues')
    .select('venues!inner(venue_name)')
    .eq('event_id', eventId)
    .limit(1)
    .maybeSingle();
  if (error) return { data: null, error };
  const venueName = (data as any)?.venues?.venue_name;
  return { data: venueName ? String(venueName) : null, error: null };

  });
}

export async function fetchEventOrderStatuses(
  eventId: string,
  options?: ClientOption,
): Promise<{ data: string[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  try {
    // Read status directly from the events table — the orders table is deprecated.
    const { data: eventRow, error: eventError } = await supabase
      .from('events')
      .select('status')
      .eq('id', eventId)
      .maybeSingle();
    if (eventError) return { data: [], error: eventError };
    const statusValue: unknown = eventRow?.status ?? null;
    if (typeof statusValue !== 'string') {
      if (statusValue == null) return { data: [], error: null };
      try {
        const coerced = String(statusValue).trim();
        return { data: coerced ? [coerced] : [], error: null };
      } catch {
        return { data: [], error: null };
      }
    }
    const trimmed = statusValue.trim();
    return { data: trimmed ? [trimmed] : [], error: null };
  } catch (err: any) {
    // Preserve prior behaviour: do not throw for header stage failures
    console.error('Failed to load event status for event header', err);
    return { data: [], error: null };
  }
}

/**
 * Fetch all active user profiles for PM and SP filter dropdowns.
 *
 * PM options are keyed by `user_profiles.id` (= events.project_manager_profile_id).
 * SP options are keyed by `user_profiles.auth_user_id` (= events.sales_manager_id).
 */
export async function fetchPmSpOptions(
  options?: ClientOption,
): Promise<{
  pmOptions: Array<{ id: string; name: string }>;
  spOptions: Array<{ id: string; name: string }>;
}> {
  const supabase = options?.supabase ?? getSupabaseClient();

  const { data } = await supabase
    .from('user_profiles')
    .select('id,auth_user_id,full_name,first_name,last_name,email,is_archived')
    .neq('is_archived', true)
    .order('full_name', { ascending: true });

  const rows = Array.isArray(data) ? (data as any[]) : [];

  const displayName = (r: any): string =>
    (r.full_name ?? '').trim()
    || [r.first_name ?? '', r.last_name ?? ''].join(' ').trim()
    || (r.email ?? '')
    || 'Unknown user';

  // PM: keyed by profile id
  const pmOptions = rows
    .filter((r) => typeof r.id === 'string' && r.id.length > 0)
    .map((r) => ({ id: r.id as string, name: displayName(r) }))
    .sort((a, b) => a.name.localeCompare(b.name));

  // SP: keyed by auth user id
  const spOptions = rows
    .filter((r) => typeof r.auth_user_id === 'string' && r.auth_user_id.length > 0)
    .map((r) => ({ id: r.auth_user_id as string, name: displayName(r) }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return { pmOptions, spOptions };
}

/* ── Event dashboard counts (lightweight RPC) ── */

export type EventDashboardCountRow = {
  site_location_id: string;
  location_name: string;
  total: number;
  upcoming: number;
  in_progress: number;
  completed: number;
  completed_amount: number;
};

export async function fetchEventDashboardCounts(
  options?: ClientOption,
): Promise<{ data: EventDashboardCountRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('get_event_dashboard_counts');
  if (error) {
    console.error('[fetchEventDashboardCounts] RPC error:', error.message, error.code, error.details, error.hint);
    return { data: [], error };
  }
  const rows = Array.isArray(data) ? (data as EventDashboardCountRow[]) : [];
  return { data: rows, error: null };
}

export function subscribeToOrderStatusChanges(params: {
  eventId: string;
  onChange: () => void;
  supabase?: SupabaseClient;
}): () => void {
  const supabase = params.supabase ?? getSupabaseClient();
  if (!params.eventId) return () => undefined;
  let active = true;
  const channel = supabase
    .channel(`events-stage-header:${params.eventId}`)
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'events', filter: `id=eq.${params.eventId}` },
      () => {
        if (active) params.onChange();
      },
    )
    .subscribe();
  return () => {
    active = false;
    try {
      supabase.removeChannel(channel);
    } catch {
      /* noop */
    }
  };
}
